import { useState } from 'react';
import { App, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { errorMessage } from '../api/client';

const CREATE = '__create__';

export interface CreatableOption {
  noun: string;
  pending: boolean;
  /** Creates the item and resolves to its id. */
  run: (name: string) => Promise<string>;
}

interface BaseProps {
  items: { id: string; name: string; path?: string }[] | undefined;
  placeholder?: string;
  ariaLabel?: string;
  /** When given, typing a name that doesn't exist offers to create it. */
  create?: CreatableOption;
}
interface MultiProps extends BaseProps { multiple: true; value?: string[]; onChange?: (v: string[]) => void }
interface SingleProps extends BaseProps { multiple?: false; value?: string | null; onChange?: (v: string | null) => void }

/** Select over tags, lists or segments, optionally creating a new one from the search text. Works inside a Form.Item. */
export function CreatableSelect(props: MultiProps | SingleProps) {
  const { items, placeholder, ariaLabel, create } = props;
  const { message } = App.useApp();
  const [search, setSearch] = useState('');
  const name = search.trim();
  const canCreate = !!create && !!name && !(items ?? []).some((i) => (i.path ?? i.name).toLowerCase() === name.toLowerCase());
  const options = [
    ...(items ?? []).map((i) => ({ value: i.id, label: i.path ?? i.name })),
    ...(canCreate ? [{ value: CREATE, label: <span><PlusOutlined /> Create {create.noun} "{name}"</span>, title: name }] : []),
  ];
  const onSelect = async (v: string | undefined) => {
    if (v !== CREATE || !create) return;
    try {
      const id = await create.run(name);
      if (props.multiple) props.onChange?.([...(props.value ?? []), id]);
      else props.onChange?.(id);
      setSearch('');
      message.success(`Created ${create.noun} "${name}"`);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };
  return (
    <Select
      mode={props.multiple ? 'multiple' : undefined}
      allowClear={!props.multiple}
      showSearch
      style={{ width: '100%' }}
      placeholder={placeholder}
      aria-label={ariaLabel}
      value={props.value ?? (props.multiple ? [] : undefined)}
      onChange={(v: string | string[] | undefined) => {
        if (props.multiple) props.onChange?.((v as string[]).filter((x) => x !== CREATE));
        else if (v !== CREATE) props.onChange?.((v as string | undefined) ?? null);
      }}
      onSelect={(v: string | undefined) => void onSelect(v)}
      searchValue={search}
      onSearch={setSearch}
      filterOption={(input, o) => o?.value === CREATE || String(o?.label ?? '').toLowerCase().includes(input.toLowerCase())}
      loading={create?.pending}
      options={options}
    />
  );
}
